import { useEffect, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';

import { Box } from '@mui/material';

import UsersTableDataRow from './UsersTableDataRow';

import { getUsers, getUserRoles } from '../../api/usersApi/usersApi';
import TableContainer from '../../components/Table/TableContainer';
import Loader from '../../components/Loader';
import { LAWYERS_LIST_TABLE_LABELS } from '../../constants/tableLabels';
import usePagination from '../../hooks/usePagination';
import { ActiveSortField, FilterTypes, SelectOption, User } from '../../types';
import { useHasPermission } from '../../hooks/useHasPermission';
import NoDataText from '../../components/NoDataText';
import TableFilters from '../../components/TableFilters/TableFilters';
import TableActions from '../../components/Table/TableActions';
import { downloadCsvorExcel } from '../../helpers/downloadCsv';
import useSnackBar from '../../hooks/useSnackBar';
import { handleSortTableData } from '../../helpers/handleSortTableData';
import { USERS_STATUS_FILTER_OPTIONS } from '../../components/TableFilters/constants';

// The users list shows only active users on load, until the Status filter is changed or cleared
const DEFAULT_USER_STATUS_FILTER = 'Active';

const UsersView = () => {
  const params = useParams();
  const firmId = localStorage.getItem('firmId');

  const { pageNumber, rowsPerPage, setTotalCount } = usePagination();
  const { setAlert } = useSnackBar();
  const canCreate = useHasPermission('Users', 'Create');

  const [lawyersList, setLawyersList] = useState<User[]>([]);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [showFilters, setShowFilters] = useState<boolean>(true);
  const [roleOptions, setRoleOptions] = useState<SelectOption[]>([]);

  useEffect(() => {
    getUserRoles().then((res) => {
      if (res.data?.roles) {
        setRoleOptions(res.data.roles.map((r) => ({ id: String(r.id), label: r.roleName })));
      }
    });
  }, []);

  const hasParams = !!Object.entries(params).length;

  const [filters, setFilters] = useState<FilterTypes>({
    firmId: firmId || '',
    status: DEFAULT_USER_STATUS_FILTER,
  });
  const [match, setMatch] = useState<string>('');
  const [activeSortField, setActiveSortField] = useState<ActiveSortField | null>(null);
  const sortableFields = {
    name: {
      order: '',
      fieldName: 'name',
    },
    createdAt: {
      order: '',
      fieldName: 'createdAt',
    },
     updatedAt: {
      order: '',
      fieldName: 'updatedAt',
    },
  };

  useEffect(() => {
    getUsers({
      page: pageNumber,
      size: rowsPerPage,
      count: pageNumber === 1,
      match: match,
      filters: { ...filters, ...(hasParams && params) },
      activeSortField,
    }).then((res) => {
      if (res.data) {
        pageNumber === 1 && setTotalCount?.(res.data.totalCount || 0);
        setLawyersList(res.data.resultSet);
        setIsDataLoading(false);
      }
    });
  }, [pageNumber, rowsPerPage, filters, match, activeSortField]);

  const exportList = () => {
    downloadCsvorExcel('/v1/firm-users/export', 'POST', 'Users_List','csv', {
      page: pageNumber,
      size: rowsPerPage,
      count: true,
      match: match,
      filters: filters,
    }).then((res) => {
      if (res?.data) {
        setAlert({
          message: 'Users exported successfully.',
          type: 'success',
        });
      }
    });
  };

  return (
    <>
      {isDataLoading && !lawyersList.length ? (
        <Box position="absolute" top="50%" left="50%">
          <Loader colorType="warning" />
        </Box>
      ) : (
        <>
          <TableActions
            hasParams={hasParams}
            createLink={canCreate ? '/users/create-user' : undefined}
            exportList={exportList}
            match={match}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            setMatch={setMatch}
          />

          {showFilters && (
            <TableFilters
              filters={filters}
              setFilters={setFilters}
              setMatch={setMatch}
              timeFilter={'Created'}
              match={match}
              rolesFilter={roleOptions}
              statusFilter={USERS_STATUS_FILTER_OPTIONS}
              defaultStatus={DEFAULT_USER_STATUS_FILTER}
            />
          )}

          {!isDataLoading && lawyersList.length === 0 ? (
            <NoDataText />
          ) : (
            <TableContainer
              headerLabels={LAWYERS_LIST_TABLE_LABELS}
              sortableFields={sortableFields}
              activeSortField={activeSortField}
              setActiveSortField={setActiveSortField}
              handleSortTableData={handleSortTableData}
            >
              <UsersTableDataRow tableData={lawyersList} />
            </TableContainer>
          )}
        </>
      )}

      <Outlet />
    </>
  );
};

export default UsersView;
